import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { can, normRole } from "@/lib/permissions";
import { Hammer, PlayCircle, CheckCircle2, PauseCircle, ShieldCheck, ArrowRight, AlertTriangle, ArrowLeft } from "lucide-react";

const STAGES = ["CAM", "Wax", "Casting", "Filing", "Setting", "Polish", "QC", "Tagging", "Dispatch"];

const STATUS_BADGE = {
  pending: "bg-[#F1EDDF] text-[#4A5A50]",
  in_progress: "bg-[#FFF3D6] text-[#7A4F08]",
  done: "bg-[#E1F0E6] text-[#0F5E39]",
  on_hold: "bg-[#FCE4EC] text-[#7A0D46]",
  rework: "bg-red-50 text-red-700",
  skipped: "bg-[#F1EDDF] text-[#4A5A50] italic",
};

// ============ PRODUCTION BOARD ============
export function ProductionBoardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [board, setBoard] = useState({});
  const canSeeClient = can(user, "production.see_client");

  const load = async () => {
    try { const { data } = await api.get("/production/board"); setBoard(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const columns = [{ key: "pre_production", label: "Pre-production", tint: "text-[#4A5A50]" }, ...STAGES.map(s => ({ key: s, label: s, tint: "text-[#0F5E39]" }))];

  return (
    <div className="space-y-6" data-testid="production-board-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Manufacturing</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">Production Board</h1>
        <p className="text-[#4A5A50] mt-1">All items across the pipeline — from CAM to Dispatch.</p>
      </div>

      <div className="grid grid-flow-col auto-cols-[minmax(240px,1fr)] gap-3 overflow-x-auto pb-4">
        {columns.map(col => {
          const jobs = board[col.key] || [];
          return (
            <div key={col.key} className="bg-[#F1EDDF] rounded-xl p-3" data-testid={`col-${col.key}`}>
              <div className="flex items-center justify-between mb-2 px-1">
                <div className={`text-[10px] uppercase tracking-widest font-semibold ${col.tint}`}>{col.label}</div>
                <div className="text-xs text-[#4A5A50]">{jobs.length}</div>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {jobs.map(j => (
                  <button key={j.id} onClick={() => nav(`/production/jobs/${j.id}`)} className="w-full text-left bg-white rounded-lg border border-[#E4DFCF] p-3 hover:shadow-md" data-testid={`job-card-${j.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{j.product_category_name || "—"}</div>
                    <div className="font-serif text-base mt-0.5">{j.order_reference}</div>
                    {canSeeClient && j.order_reference && (
                      <div className="text-xs text-[#4A5A50] mt-0.5">Item #{(j.order_item_index ?? 0) + 1}</div>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                      {j.karat && <span className="bg-[#F6EED8] text-[#6E5416] px-2 py-0.5 rounded-full font-medium">{j.karat}</span>}
                      {j.stone_type && j.stone_type !== "none" && <span className="bg-[#E1F0E6] text-[#0F5E39] px-2 py-0.5 rounded-full">{j.stone_type}</span>}
                      {j.weight_grams != null && <span className="text-[#4A5A50]">{j.weight_grams}g</span>}
                    </div>
                    {j.not_producible && <div className="mt-2 text-[10px] text-red-700 flex items-center gap-1"><AlertTriangle size={12}/> Not producible</div>}
                  </button>
                ))}
                {jobs.length === 0 && <div className="text-xs text-[#4A5A50] italic px-2 py-4">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ MY QUEUE (production users) ============
export function MyQueuePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/production/my-queue"); setJobs(data); }
    catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="my-queue-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">{user?.department || "Production"} Queue</div>
        <h1 className="font-serif text-3xl lg:text-4xl font-medium">My Queue</h1>
        <p className="text-[#4A5A50] mt-1">Items currently at your department. Customer identity and pricing are not shown here.</p>
      </div>

      {jobs.length === 0 ? (
        <Card className="border-[#E4DFCF] rounded-xl">
          <CardContent className="py-16 text-center">
            <Hammer size={32} className="text-[#C9A24B] mx-auto mb-3"/>
            <div className="font-serif text-xl">Nothing in your queue</div>
            <div className="text-[#4A5A50] text-sm mt-1">Come back later — new work will land here.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map(j => (
            <button key={j.id} onClick={() => nav(`/production/jobs/${j.id}`)} className="text-left bg-white rounded-xl border border-[#E4DFCF] p-5 card-hover" data-testid={`queue-job-${j.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-serif text-xl">{j.order_reference}</div>
                <StatusPill status={j.milestone === "ready_dispatched" ? "completed" : "in-progress"}/>
              </div>
              <div className="text-sm text-[#4A5A50] mt-1">{j.product_category_name} · Item #{(j.order_item_index ?? 0) + 1}</div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {j.karat && <span className="bg-[#F6EED8] text-[#6E5416] px-2 py-1 rounded-full font-medium">{j.karat}</span>}
                {j.metal_kind && <span className="bg-[#F1EDDF] text-[#4A5A50] px-2 py-1 rounded-full">{j.metal_kind}</span>}
                {j.stone_type && j.stone_type !== "none" && <span className="bg-[#E1F0E6] text-[#0F5E39] px-2 py-1 rounded-full">{j.stone_type}</span>}
                {j.weight_grams != null && <span className="text-[#4A5A50]">gm {j.weight_grams}</span>}
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-[#0F5E39] flex items-center gap-1">Open <ArrowRight size={12}/></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ JOB DETAIL ============
export function ProductionJobDetailPage() {
  const { user } = useAuth();
  const { id: jobId } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [action, setAction] = useState(null); // {stage, kind}
  const [gateOpen, setGateOpen] = useState(null); // stage_key
  const [specOpen, setSpecOpen] = useState(false);
  const canSpec = can(user, "production.spec");
  const canSeeClient = can(user, "production.see_client");
  const role = normRole(user?.role);
  const isProdUser = role === "production";
  const canReassign = ["admin", "super-admin", "manager"].includes(role);
  const [reassignFor, setReassignFor] = useState(null); // stage key

  const load = async () => {
    try { const { data } = await api.get(`/production/jobs/${jobId}`); setJob(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Not found"); nav(-1); }
  };
  useEffect(() => { load(); }, [jobId]);

  if (!job) return <div className="text-center py-16 text-[#4A5A50]">Loading…</div>;

  const myDept = user?.department;

  const runAction = async (stageKey, kind, extra = {}) => {
    try {
      await api.post(`/production/jobs/${jobId}/stage/${stageKey}`, { action: kind, ...extra });
      toast.success(`Stage ${stageKey} · ${kind}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="job-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Production Job</div>
          <h1 className="font-serif text-3xl font-medium">{job.order_reference} · Item #{(job.order_item_index ?? 0) + 1}</h1>
          <div className="text-sm text-[#4A5A50] mt-1">{job.product_category_name || "—"}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Current stage</div>
          <div className="font-serif text-2xl text-[#0F5E39]">{job.current_stage || "Pre-production"}</div>
          <div className="text-xs text-[#4A5A50] mt-1">Milestone: {(job.milestone || "").replace(/_/g, " ")}</div>
        </div>
      </div>

      {/* Spec strip */}
      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm flex-1">
              <Spec label="Karat" value={job.karat}/>
              <Spec label="Metal" value={job.metal_kind}/>
              <Spec label="Stone" value={job.stone_type}/>
              <Spec label="Weight" value={job.weight_grams != null ? `${job.weight_grams} g` : "—"}/>
              <Spec label="Design source" value={(job.design_source || "").replace(/_/g, " ")}/>
            </div>
            {canSpec && (
              <Button size="sm" variant="outline" onClick={() => setSpecOpen(true)} className="rounded-full" data-testid="edit-spec-btn">
                Edit spec
              </Button>
            )}
          </div>
          {job.not_producible && (
            <div className="mt-3 p-2 bg-red-50 text-red-700 text-xs rounded flex items-center gap-2">
              <AlertTriangle size={14}/> Feasibility failed: {job.feasibility_remarks}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pipeline stages */}
      <div className="space-y-3">
        {(job.stages || []).map(s => {
          const canAct = !isProdUser || s.department === myDept;
          const gateForStage = { Wax: "wax_size_check", Casting: "post_casting_check", QC: "qc_final" }[s.key];
          return (
            <Card key={s.key} className={`border-[#E4DFCF] rounded-xl ${s.status === "in_progress" ? "border-l-4 border-l-[#C9A24B]" : ""}`} data-testid={`stage-${s.key}`}>
              <CardContent className="p-5 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-serif text-lg">{s.key}</div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest font-semibold ${STATUS_BADGE[s.status]}`}>{(s.status || "").replace("_", " ")}</span>
                  </div>
                  <div className="text-xs text-[#4A5A50] mt-0.5">Dept: {s.department}{s.assigned_user_name ? ` · ${s.assigned_user_name}` : ""}</div>
                  {s.remarks && <div className="text-xs text-[#4A5A50] italic mt-1">"{s.remarks}"</div>}
                  {s.gate && (
                    <div className={`mt-2 text-xs inline-flex items-center gap-1 px-2 py-1 rounded ${s.gate.result === "pass" ? "bg-[#E1F0E6] text-[#0F5E39]" : s.gate.result === "fail" ? "bg-red-50 text-red-700" : "bg-[#F1EDDF] text-[#4A5A50]"}`}>
                      <ShieldCheck size={12}/> Gate: {s.gate.result} {s.gate.remarks ? `· ${s.gate.remarks}` : ""}
                    </div>
                  )}
                </div>
                {canAct && s.status !== "done" && s.status !== "skipped" && (
                  <div className="flex flex-wrap gap-2">
                    {s.status === "pending" && (
                      <Button size="sm" onClick={() => runAction(s.key, "start")} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid={`stage-start-${s.key}`}>
                        <PlayCircle size={14} className="mr-1"/> Start
                      </Button>
                    )}
                    {(s.status === "in_progress" || s.status === "rework") && (
                      <>
                        <Button size="sm" onClick={() => setAction({ stage: s.key, kind: "complete" })} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid={`stage-complete-${s.key}`}>
                          <CheckCircle2 size={14} className="mr-1"/> Complete
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => runAction(s.key, "hold", { remarks: "On hold" })} className="rounded-full">
                          <PauseCircle size={14} className="mr-1"/> Hold
                        </Button>
                        {gateForStage && (
                          <Button size="sm" variant="outline" onClick={() => setGateOpen(s.key)} className="rounded-full border-[#C9A24B] text-[#6E5416] hover:bg-[#F6EED8]" data-testid={`stage-gate-${s.key}`}>
                            <ShieldCheck size={14} className="mr-1"/> Inspection
                          </Button>
                        )}
                      </>
                    )}
                    {s.status === "on_hold" && (
                      <Button size="sm" onClick={() => runAction(s.key, "resume")} variant="outline" className="rounded-full">
                        <PlayCircle size={14} className="mr-1"/> Resume
                      </Button>
                    )}
                    {canReassign && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReassignFor(s.key)}
                        className="rounded-full border-[#C9A24B] text-[#6E5416] hover:bg-[#F6EED8]"
                        data-testid={`stage-reassign-${s.key}`}
                      ><Hammer size={14} className="mr-1"/> Reassign</Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Timeline */}
      <Card className="border-[#E4DFCF] rounded-xl">
        <CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mb-3">Timeline</div>
          <div className="space-y-2">
            {(job.logs || []).map(l => (
              <div key={l.id} className="flex items-start gap-3 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-[#C9A24B] mt-2"/>
                <div className="flex-1">
                  <div><span className="font-medium">{(l.action || "").replace(/_/g, " ")}</span>{l.from_stage && <span className="text-[#4A5A50]"> · {l.from_stage}{l.to_stage ? ` → ${l.to_stage}` : ""}</span>}</div>
                  {l.remarks && <div className="text-xs text-[#4A5A50] italic">"{l.remarks}"</div>}
                  <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mt-0.5">{l.user_name} · {format(new Date(l.created_at), "dd MMM, HH:mm")}</div>
                </div>
              </div>
            ))}
            {(!job.logs || job.logs.length === 0) && <div className="text-sm text-[#4A5A50]">No activity yet</div>}
          </div>
        </CardContent>
      </Card>

      {/* Complete dialog */}
      <Dialog open={!!action} onOpenChange={() => setAction(null)}>
        <DialogContent data-testid="action-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Complete {action?.stage}</DialogTitle></DialogHeader>
          <CompleteStageForm stage={action?.stage} onSubmit={(extra) => { runAction(action.stage, "complete", extra); setAction(null); }} onCancel={() => setAction(null)}/>
        </DialogContent>
      </Dialog>

      {/* Gate dialog */}
      <Dialog open={!!gateOpen} onOpenChange={() => setGateOpen(null)}>
        <DialogContent data-testid="gate-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Inspection · {gateOpen}</DialogTitle></DialogHeader>
          <GateForm stageKey={gateOpen} onSubmit={async (payload) => {
            try { await api.post(`/production/jobs/${jobId}/gate`, payload); toast.success("Inspection recorded"); setGateOpen(null); load(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
          }} onCancel={() => setGateOpen(null)}/>
        </DialogContent>
      </Dialog>

      {/* Spec dialog */}
      {canSpec && (
        <Dialog open={specOpen} onOpenChange={setSpecOpen}>
          <DialogContent data-testid="spec-dialog">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Confirm specifications</DialogTitle></DialogHeader>
            <SpecForm job={job} onSubmit={async (payload) => {
              try { await api.post(`/production/jobs/${jobId}/spec`, payload); toast.success("Spec confirmed"); setSpecOpen(false); load(); }
              catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
            }} onCancel={() => setSpecOpen(false)}/>
          </DialogContent>
        </Dialog>
      )}

      {canReassign && (
        <KarigarReassignDialog
          open={!!reassignFor}
          stageKey={reassignFor}
          currentAssignedId={(job.stages || []).find(s => s.key === reassignFor)?.assigned_user_id}
          onClose={() => setReassignFor(null)}
          onSaved={async (uid) => {
            try {
              await api.patch(`/production/jobs/${jobId}/stages/${reassignFor}/karigar`, { karigar_id: uid });
              toast.success("Karigar updated");
              setReassignFor(null);
              load();
            } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
          }}
        />
      )}
    </div>
  );
}


function KarigarReassignDialog({ open, stageKey, currentAssignedId, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/production/karigar-workload");
        setRows(data.items || []);
      } catch (e) { toast.error("Could not load karigars"); }
      finally { setLoading(false); }
    })();
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="karigar-reassign-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Reassign <span className="text-[#0F5E39]">{stageKey}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-[#4A5A50] mb-2">
          Sorted lightest workload first — pick a karigar to hand the stage over to. Green means low load.
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-[#E4DFCF] rounded-lg border border-[#E4DFCF]">
          {loading && <div className="p-3 text-xs text-[#4A5A50]">Loading…</div>}
          {!loading && rows.length === 0 && <div className="p-3 text-xs text-[#4A5A50]">No production users found.</div>}
          {rows.map((u) => {
            const isCurrent = u.id === currentAssignedId;
            const tone = u.active_stage_count === 0
              ? "bg-[#E1F0E6] text-[#0F5E39]"
              : u.active_stage_count < 4
                ? "bg-[#F6EED8] text-[#6E5416]"
                : "bg-red-50 text-red-700";
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => onSaved(u.id)}
                className={`w-full text-left p-3 flex items-center justify-between gap-3 hover:bg-[#FAF7EF] ${isCurrent ? "bg-[#F6EED8]/60" : ""}`}
                data-testid={`karigar-option-${u.id}`}
              >
                <div>
                  <div className="text-sm text-[#0F1E15]">{u.name}{isCurrent ? " · current" : ""}</div>
                  <div className="text-[11px] text-[#4A5A50]">{u.department || "—"}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${tone}`}>
                  {u.active_stage_count} open
                </span>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onSaved(null)} data-testid="karigar-unassign">
            Unassign
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{label}</div>
      <div className="font-medium">{value || "—"}</div>
    </div>
  );
}

function CompleteStageForm({ stage, onSubmit, onCancel }) {
  const [remarks, setRemarks] = useState("");
  const [extra, setExtra] = useState({});
  const isPolish = stage === "Polish";
  const isTagging = stage === "Tagging";
  const isCasting = stage === "Casting";
  return (
    <div className="space-y-3">
      <div><Label>Remarks</Label><Textarea rows={2} value={remarks} onChange={(e)=>setRemarks(e.target.value)} className="mt-1" data-testid="stage-complete-remarks"/></div>
      {isCasting && (
        <div><Label>Casting date</Label><Input type="date" onChange={(e)=>setExtra({...extra, casting_date: e.target.value ? new Date(e.target.value).toISOString() : null})} className="mt-1"/></div>
      )}
      {isPolish && <>
        <div><Label>Finish type</Label>
          <Select onValueChange={(v)=>setExtra({...extra, finish_type:v})}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Pick"/></SelectTrigger>
            <SelectContent><SelectItem value="rhodium">Rhodium</SelectItem><SelectItem value="polish">Polish</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" onChange={(e)=>setExtra({...extra, laser_marking: e.target.checked})}/> Laser marking</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" onChange={(e)=>setExtra({...extra, anti_tarnish: e.target.checked})}/> Anti-tarnish</label>
        </div>
      </>}
      {isTagging && (
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Tag ID</Label><Input onChange={(e)=>setExtra({...extra, tag_id: e.target.value})} className="mt-1"/></div>
          <div><Label>Final weight (g)</Label><Input type="number" step="0.01" onChange={(e)=>setExtra({...extra, final_weight: Number(e.target.value)})} className="mt-1"/></div>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit({ remarks, ...extra })} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="complete-submit">Save & Advance</Button>
      </DialogFooter>
    </div>
  );
}

function GateForm({ stageKey, onSubmit, onCancel }) {
  const gateKey = { Wax: "wax_size_check", Casting: "post_casting_check", QC: "qc_final" }[stageKey];
  const [result, setResult] = useState("pass");
  const [remarks, setRemarks] = useState("");
  const [measured, setMeasured] = useState("");
  const [rework, setRework] = useState("CAM");
  return (
    <div className="space-y-3">
      <div><Label>Result</Label>
        <div className="mt-1 flex gap-2">
          <button onClick={()=>setResult("pass")} className={`px-4 py-2 rounded-full text-sm font-medium ${result === "pass" ? "bg-[#0F5E39] text-white" : "bg-[#F1EDDF] text-[#4A5A50]"}`} data-testid="gate-pass-btn">Pass</button>
          <button onClick={()=>setResult("fail")} className={`px-4 py-2 rounded-full text-sm font-medium ${result === "fail" ? "bg-red-600 text-white" : "bg-[#F1EDDF] text-[#4A5A50]"}`} data-testid="gate-fail-btn">Fail</button>
        </div>
      </div>
      <div><Label>Measured values</Label><Input value={measured} onChange={(e)=>setMeasured(e.target.value)} className="mt-1" placeholder="e.g. 4.6g, 12mm dia"/></div>
      <div><Label>Remarks</Label><Textarea rows={2} value={remarks} onChange={(e)=>setRemarks(e.target.value)} className="mt-1" data-testid="gate-remarks"/></div>
      {result === "fail" && (
        <div><Label>Send back for rework at</Label>
          <Select value={rework} onValueChange={setRework}>
            <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit({ gate_key: gateKey, result, remarks, measured_values: { measured }, rework_target_stage: result === "fail" ? rework : null })} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="gate-submit">Save</Button>
      </DialogFooter>
    </div>
  );
}

function SpecForm({ job, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    weight_grams: job.weight_grams || "",
    karat: job.karat || "22kt",
    metal_kind: job.metal_kind || "gold",
    stone_type: job.stone_type || "none",
    size_notes: job.size_notes || "",
  });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Weight (g)</Label><Input type="number" step="0.01" value={form.weight_grams} onChange={(e)=>setForm({...form, weight_grams:e.target.value})} className="mt-1" data-testid="spec-weight"/></div>
        <div><Label>Karat</Label>
          <Select value={form.karat} onValueChange={(v)=>setForm({...form, karat:v})}>
            <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>{["9kt","14kt","18kt","20kt","21kt","22kt","24kt"].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Metal</Label>
          <Select value={form.metal_kind} onValueChange={(v)=>setForm({...form, metal_kind:v})}>
            <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>{["gold","silver","platinum"].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Stones</Label>
          <Select value={form.stone_type} onValueChange={(v)=>setForm({...form, stone_type:v})}>
            <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>{["none","CZ","lab_diamond","real_diamond"].map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Size notes</Label><Input value={form.size_notes} onChange={(e)=>setForm({...form, size_notes:e.target.value})} className="mt-1"/></div>
      <div className="text-xs text-[#4A5A50]">Confirming will trigger the CAM stage. Setting will auto-skip when Stones = none.</div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit({ ...form, weight_grams: Number(form.weight_grams) || 0 })} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="spec-submit">Confirm spec</Button>
      </DialogFooter>
    </div>
  );
}
